import { Button, Select } from "north.js";
import React from "react";

import { HardwareRequestStatus } from "src/api/types.generated";
import { errorToast } from "src/shared/components";
import { useReturnHackerHardwareRequestMutation } from "src/views/hardware/graphql/returnHackerHardwareRequest.generated";
import { useAcceptHardwareRequestIfInStock } from "src/views/hardware/shared/useAcceptHardwareRequestIfInStock";

import {
  StatusUpdateContainer,
  RequestActions as RequestActionsStyled,
} from "./styles";
import { TRequestActionsProps } from "./types";
import { getAvailableStatuses, getStatusDisplayName } from "./utils";

const RequestActions: React.FC<TRequestActionsProps> = ({
  requestId,
  requestData,
  status,
  userType,
  onPrintLabel,
  onUpdateStatus,
  onRefresh,
  onReturnSuccess,
  isLoading = false,
  printerEndpoint,
  printingRequests,
  updatingRequests,
  selectedStatuses,
  onStatusChange,
}) => {
  const requestIdNum = parseInt(requestId);
  const [returnHackerHardwareRequest] =
    useReturnHackerHardwareRequestMutation();
  const { acceptIfInStock } = useAcceptHardwareRequestIfInStock();

  const availableStatuses =
    userType === "organizer" ? getAvailableStatuses(status, userType) : [];
  const selectedStatus = selectedStatuses[requestIdNum] ?? availableStatuses[0];

  const handleReturnHardware = async () => {
    try {
      await returnHackerHardwareRequest({
        variables: {
          id: requestIdNum,
        },
      });
      onReturnSuccess?.(requestIdNum);
    } catch (error) {
      console.error("Error returning hardware:", error);
    }
  };

  const handleAcceptRequest = async () => {
    try {
      await acceptIfInStock(requestIdNum);
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      errorToast(
        (error as Error)?.message || "Error accepting hardware request"
      );
    }
  };

  return (
    <RequestActionsStyled>
      {userType === "organizer" && printerEndpoint && onPrintLabel && (
        <Button
          size="sm"
          color="secondary"
          onClick={() => onPrintLabel(requestIdNum, requestData)}
          disabled={printingRequests.has(requestIdNum) || isLoading}
        >
          {printingRequests.has(requestIdNum) ? "Printing..." : "Print Label"}
        </Button>
      )}

      <StatusUpdateContainer>
        {/* Show specific buttons based on status and user type */}
        {userType === "organizer" &&
          status === HardwareRequestStatus.Requested && (
            <>
              <Button
                size="sm"
                color="primary"
                onClick={handleAcceptRequest}
                disabled={updatingRequests.has(requestIdNum) || isLoading}
              >
                {updatingRequests.has(requestIdNum)
                  ? "Updating..."
                  : "Accept Request"}
              </Button>
              <Button
                size="sm"
                color="danger"
                onClick={() =>
                  onUpdateStatus(requestIdNum, HardwareRequestStatus.Denied)
                }
                disabled={updatingRequests.has(requestIdNum) || isLoading}
              >
                {updatingRequests.has(requestIdNum)
                  ? "Updating..."
                  : "Deny Request"}
              </Button>
            </>
          )}
        {userType === "organizer" && status === HardwareRequestStatus.Accepted && (
          <Button
            size="sm"
            color="primary"
            onClick={() =>
              onUpdateStatus(requestIdNum, HardwareRequestStatus.Ready)
            }
            disabled={updatingRequests.has(requestIdNum) || isLoading}
          >
            {updatingRequests.has(requestIdNum)
              ? "Updating..."
              : "Mark as Ready"}
          </Button>
        )}
        {userType === "organizer" &&
          (status === HardwareRequestStatus.Accepted ||
            status === HardwareRequestStatus.Ready) && (
            <Button
              size="sm"
              color="secondary"
              onClick={() =>
                onUpdateStatus(requestIdNum, HardwareRequestStatus.PickedUp)
              }
              disabled={updatingRequests.has(requestIdNum) || isLoading}
            >
              {updatingRequests.has(requestIdNum)
                ? "Updating..."
                : "Mark as Picked Up"}
            </Button>
          )}
        {userType === "organizer" && status === HardwareRequestStatus.PickedUp && (
          <Button
            size="sm"
            color="secondary"
            onClick={handleReturnHardware}
            disabled={updatingRequests.has(requestIdNum) || isLoading}
          >
            {updatingRequests.has(requestIdNum)
              ? "Updating..."
              : "Mark as Returned"}
          </Button>
        )}

        {/* Hacker buttons - limited to canceling REQUESTED orders only */}
        {userType === "hacker" && status === HardwareRequestStatus.Requested && (
          <Button
            size="sm"
            color="danger"
            onClick={() =>
              onUpdateStatus(requestIdNum, HardwareRequestStatus.Cancelled)
            }
            disabled={updatingRequests.has(requestIdNum) || isLoading}
          >
            {updatingRequests.has(requestIdNum)
              ? "Updating..."
              : "Cancel Request"}
          </Button>
        )}

        {/* Dropdown for any status change */}
        {availableStatuses.length > 0 && (
          <StatusUpdateContainer>
            <Select
              size="sm"
              placeholder="Change status..."
              value={selectedStatus}
              onChange={(e) =>
                onStatusChange(
                  requestIdNum,
                  e.target.value as HardwareRequestStatus
                )
              }
              style={{ minWidth: "140px" }}
            >
              {availableStatuses.map((statusOption) => (
                <option key={statusOption} value={statusOption}>
                  {getStatusDisplayName(statusOption)}
                </option>
              ))}
            </Select>
            <Button
              size="sm"
              color="secondary"
              onClick={() => onUpdateStatus(requestIdNum, selectedStatus)}
              disabled={updatingRequests.has(requestIdNum) || isLoading}
            >
              {updatingRequests.has(requestIdNum) ? "Updating..." : "Update"}
            </Button>
          </StatusUpdateContainer>
        )}
      </StatusUpdateContainer>
    </RequestActionsStyled>
  );
};

export default RequestActions;
